import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { useSiteContent } from '../context/SiteContentContext';
import axios from 'axios';
import { Phone, Mail, MapPin, Facebook, Instagram, Globe, Star, Send, Loader2, CheckCircle } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const LandingPage = () => {
  const { source: paramSource } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const { content, loading } = useSiteContent();
  const [tracked, setTracked] = useState(false);
  const [showForm, setShowForm] = useState(false);

  // Form state
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    service: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [formError, setFormError] = useState('');

  // Determine source from URL path
  const source = paramSource || (location.pathname === '/koda' ? 'koda' : location.pathname === '/vizitka' ? 'vizitka' : null);

  useEffect(() => {
    // Track the visit
    const trackVisit = async () => {
      if (!tracked && (source === 'koda' || source === 'vizitka')) {
        try {
          await axios.post(`${BACKEND_URL}/api/track-visit?source=${source}`);
          setTracked(true);
        } catch (error) {
          console.error('Error tracking visit:', error);
        }
      }
    };

    trackVisit();
  }, [source, tracked]);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setFormError('');
    
    try {
      await axios.post(`${BACKEND_URL}/api/inquiries`, {
        first_name: formData.firstName,
        last_name: formData.lastName,
        email: formData.email,
        phone: formData.phone,
        service: formData.service,
        message: formData.message
      });
      
      setSubmitted(true);
      setFormData({ firstName: '', lastName: '', email: '', phone: '', service: '', message: '' });
      
      setTimeout(() => {
        setSubmitted(false);
        setShowForm(false);
      }, 5000);
    } catch (err) {
      console.error('Error submitting form:', err);
      setFormError('Prišlo je do napake. Prosimo, poskusite znova.');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Get primary phone number for call button
  const getPrimaryPhone = () => {
    const phoneNumbers = content.phone_numbers || [];
    if (phoneNumbers.length > 0 && phoneNumbers[0].number) {
      return phoneNumbers[0].number.replace(/\s/g, '');
    }
    return '';
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#2D4A5E]">
        <div className="animate-pulse text-white text-xl">Nalaganje...</div>
      </div>
    );
  }

  const landingSettings = content.landing_page || {};
  const phoneNumbers = content.phone_numbers || [];
  const serviceTypes = content.service_types || [];

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#2D4A5E] to-[#1a2f3d]">
      {/* Hero Section */}
      <section className="relative flex flex-col items-center justify-center px-4 py-8">
        {/* Background Image */}
        {landingSettings.background_image && (
          <div className="absolute inset-0">
            <img
              src={landingSettings.background_image}
              alt="Background"
              className="w-full h-full object-cover opacity-20"
            />
          </div>
        )}

        <div className="relative z-10 text-center max-w-md mx-auto w-full">
          {/* Logo */}
          <div className="mb-4">
            <img
              src={content.logo_url}
              alt={content.company_name}
              className="h-16 mx-auto"
            />
          </div>

          {/* Title */}
          <h1 className="text-3xl font-black text-white mb-2">
            {landingSettings.title || 'Dobrodošli!'}
          </h1>

          {/* Subtitle */}
          <p className="text-base text-[#4CAF50] font-semibold mb-1">
            Ste nova stranka ali ste že sodelovali z nami?
          </p>

          {/* Call to action text */}
          <p className="text-white/80 text-sm mb-5">
            Pokličite nas ali pa nam pustite mnenje ⭐
          </p>

          {/* Main CTA Buttons */}
          <div className="flex gap-3 mb-5">
            {/* Call Button */}
            <a
              href={`tel:${getPrimaryPhone()}`}
              className="flex-1 flex items-center justify-center gap-2 bg-[#4CAF50] hover:bg-[#45a049] text-white font-bold py-4 px-4 rounded-xl transition-all duration-300 shadow-lg"
            >
              <Phone size={22} />
              <span>Pokliči nas</span>
            </a>

            {/* Review Button */}
            {content.google_review_url ? (
              <a
                href={content.google_review_url}
                target="_blank"
                rel="noopener noreferrer"
                className="flex-1 flex flex-col items-center justify-center gap-0.5 bg-[#F5A623] hover:bg-[#e09620] text-white font-bold py-3 px-4 rounded-xl transition-all duration-300 shadow-lg"
              >
                <div className="flex items-center gap-2">
                  <Star size={20} fill="white" />
                  <span>Oddajte mnenje</span>
                </div>
                <span className="text-xs font-normal opacity-90">(1 minuta)</span>
              </a>
            ) : (
              <button
                onClick={() => setShowForm(true)}
                className="flex-1 flex flex-col items-center justify-center gap-0.5 bg-[#F5A623] hover:bg-[#e09620] text-white font-bold py-3 px-4 rounded-xl transition-all duration-300 shadow-lg"
              >
                <div className="flex items-center gap-2">
                  <Star size={20} fill="white" />
                  <span>Oddajte mnenje</span>
                </div>
                <span className="text-xs font-normal opacity-90">(1 minuta)</span>
              </button>
            )}
          </div>

          {/* Social Links */}
          <div className="flex items-center justify-center gap-3 mb-6">
            {/* Website */}
            <a
              href="/"
              onClick={(e) => { e.preventDefault(); navigate('/'); }}
              className="flex items-center gap-2 bg-white/10 backdrop-blur-sm border border-white/20 rounded-xl px-5 py-2.5 hover:bg-white/20 transition-colors"
            >
              <Globe size={18} className="text-[#4CAF50]" />
              <span className="text-white text-sm font-medium">Splet</span>
            </a>
            
            {/* Facebook */}
            {content.facebook_url && (
              <a
                href={content.facebook_url}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-2 bg-white/10 backdrop-blur-sm border border-white/20 rounded-xl px-5 py-2.5 hover:bg-white/20 transition-colors"
              >
                <Facebook size={18} className="text-[#1877F2]" />
                <span className="text-white text-sm font-medium">Facebook</span>
              </a>
            )}
            
            {/* Instagram */}
            {content.instagram_url && (
              <a
                href={content.instagram_url}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-2 bg-white/10 backdrop-blur-sm border border-white/20 rounded-xl px-5 py-2.5 hover:bg-white/20 transition-colors"
              >
                <Instagram size={18} className="text-[#E4405F]" />
                <span className="text-white text-sm font-medium">Instagram</span>
              </a>
            )}
          </div>

          {/* Contact Section */}
          <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-5 mb-6">
            <h3 className="text-white font-bold mb-4 text-lg">Kontakt:</h3>
            
            {/* Phone Numbers as Tiles */}
            <div className="space-y-2 mb-4">
              {phoneNumbers.map((phone, index) => (
                phone.number && (
                  <a
                    key={index}
                    href={`tel:${phone.number.replace(/\s/g, '')}`}
                    className="flex items-center gap-3 bg-white/10 hover:bg-white/20 rounded-xl px-4 py-3 transition-colors"
                  >
                    <div className="bg-[#4CAF50] p-2 rounded-full">
                      <Phone size={18} className="text-white" />
                    </div>
                    <div className="text-left">
                      {phone.name && <span className="text-white/70 text-sm">{phone.name}:</span>}
                      <p className="text-white font-semibold">{phone.number}</p>
                    </div>
                  </a>
                )
              ))}
            </div>

            {/* Email */}
            <a
              href={`mailto:${content.email}`}
              className="flex items-center gap-3 text-white/80 hover:text-white transition-colors mb-3"
            >
              <Mail size={18} />
              <span>{content.email}</span>
            </a>

            {/* Address */}
            {content.address && (
              <div className="flex items-center gap-3 text-white/60">
                <MapPin size={18} />
                <span>{content.address}</span>
              </div>
            )}
          </div>

          {/* Services Preview */}
          {content.services?.length > 0 && (
            <div className="mb-6">
              <h3 className="text-white font-bold mb-4 text-lg">Naše storitve</h3>
              <div className="grid grid-cols-2 gap-3">
                {content.services.slice(0, 4).map((service) => (
                  <button
                    key={service.id}
                    onClick={() => navigate(`/storitev/${service.id}`)}
                    className="bg-white/10 backdrop-blur-sm rounded-xl overflow-hidden hover:bg-white/20 transition-colors text-left"
                  >
                    <div className="aspect-[4/3] overflow-hidden">
                      <img
                        src={service.image}
                        alt={service.title}
                        className="w-full h-full object-cover"
                      />
                    </div>
                    <div className="p-2">
                      <h4 className="text-white font-semibold text-sm">{service.title}</h4>
                    </div>
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Inquiry Form Button */}
          {!showForm && (
            <button
              onClick={() => setShowForm(true)}
              className="w-full bg-white/10 hover:bg-white/20 text-white font-semibold py-3 rounded-xl transition-colors border border-white/20 mb-6"
            >
              Pošlji povpraševanje
            </button>
          )}

          {/* Contact Form */}
          {showForm && (
            <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-5 mb-6">
              <h3 className="text-white font-bold mb-4">Povpraševanje</h3>
              
              {submitted ? (
                <div className="bg-green-500/20 border border-green-400/30 rounded-lg p-4 text-center">
                  <CheckCircle className="w-10 h-10 text-green-400 mx-auto mb-2" />
                  <p className="text-green-300 font-semibold">Hvala za vaše povpraševanje!</p>
                  <p className="text-green-400/70 text-sm">Odgovorili vam bomo v najkrajšem možnem času.</p>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-3 text-left">
                  {formError && (
                    <div className="bg-red-500/20 border border-red-400/30 text-red-300 px-3 py-2 rounded-lg text-sm">
                      {formError}
                    </div>
                  )}
                  
                  <div className="grid grid-cols-2 gap-3">
                    <input
                      type="text"
                      name="firstName"
                      value={formData.firstName}
                      onChange={handleChange}
                      required
                      placeholder="Ime"
                      className="w-full px-3 py-2.5 bg-white/10 border border-white/20 rounded-lg text-white placeholder-white/50 focus:ring-2 focus:ring-[#4CAF50] focus:border-transparent outline-none text-sm"
                    />
                    <input
                      type="text"
                      name="lastName"
                      value={formData.lastName}
                      onChange={handleChange}
                      required
                      placeholder="Priimek"
                      className="w-full px-3 py-2.5 bg-white/10 border border-white/20 rounded-lg text-white placeholder-white/50 focus:ring-2 focus:ring-[#4CAF50] focus:border-transparent outline-none text-sm"
                    />
                  </div>

                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    placeholder="Email"
                    className="w-full px-3 py-2.5 bg-white/10 border border-white/20 rounded-lg text-white placeholder-white/50 focus:ring-2 focus:ring-[#4CAF50] focus:border-transparent outline-none text-sm"
                  />

                  <input
                    type="tel"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    placeholder="Telefon (opcijsko)"
                    className="w-full px-3 py-2.5 bg-white/10 border border-white/20 rounded-lg text-white placeholder-white/50 focus:ring-2 focus:ring-[#4CAF50] focus:border-transparent outline-none text-sm"
                  />

                  <select
                    name="service"
                    value={formData.service}
                    onChange={handleChange}
                    required
                    className="w-full px-3 py-2.5 bg-white/10 border border-white/20 rounded-lg text-white focus:ring-2 focus:ring-[#4CAF50] focus:border-transparent outline-none text-sm cursor-pointer"
                  >
                    <option value="" className="bg-[#2D4A5E]">Izberite storitev</option>
                    {serviceTypes.map((type) => (
                      <option key={type.value} value={type.label} className="bg-[#2D4A5E]">
                        {type.label}
                      </option>
                    ))}
                  </select>

                  <textarea
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    required
                    rows={3}
                    placeholder="Vaše sporočilo..."
                    className="w-full px-3 py-2.5 bg-white/10 border border-white/20 rounded-lg text-white placeholder-white/50 focus:ring-2 focus:ring-[#4CAF50] focus:border-transparent outline-none text-sm resize-none"
                  />

                  <div className="flex gap-2">
                    <button
                      type="button"
                      onClick={() => setShowForm(false)}
                      className="flex-1 bg-white/10 hover:bg-white/20 text-white font-semibold px-4 py-3 rounded-lg transition-all duration-300"
                    >
                      Prekliči
                    </button>
                    <button
                      type="submit"
                      disabled={isSubmitting}
                      className="flex-1 bg-[#4CAF50] hover:bg-[#45a049] text-white font-semibold px-4 py-3 rounded-lg transition-all duration-300 flex items-center justify-center gap-2 disabled:opacity-70 disabled:cursor-not-allowed"
                    >
                      {isSubmitting ? (
                        <>
                          <Loader2 className="w-4 h-4 animate-spin" />
                          Pošiljanje...
                        </>
                      ) : (
                        <>
                          <Send className="w-4 h-4" />
                          Pošlji
                        </>
                      )}
                    </button>
                  </div>
                </form>
              )}
            </div>
          )}

          {/* Footer */}
          <p className="text-white/40 text-sm">
            © {new Date().getFullYear()} {content.company_name}
          </p>
        </div>
      </section>
    </div>
  );
};

export default LandingPage;
